import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router';

import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAuth } from '@pozi/authmodule';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { Observable, tap } from 'rxjs';
import { routes } from './app.routes';
import { ApiInfo } from './models/api-info';
import { CompanyUser, ICompanyUser } from './models/company-user';
import { blobErrorInterceptor } from './shared/error-handling/blob-error.interceptor';
import { httpErrorInterceptor } from './shared/error-handling/http-error.interceptor';
import { isChunkLoadError } from './shared/error-handling/stale-bundle/chunk-load-error';
import { StaleBundleErrorHandler } from './shared/error-handling/stale-bundle/stale-bundle-error.handler';
import { StaleBundleRecoveryService } from './shared/error-handling/stale-bundle/stale-bundle-recovery.service';
import { languageInterceptor } from './shared/language/language.interceptor';
import { SharedVariablesService } from './shared/shared-variables/shared-variables.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withNavigationErrorHandler((event: NavigationError) => {
        if (isChunkLoadError(event.error)) {
          inject(StaleBundleRecoveryService).recover();
        }
      })
    ),
    { provide: ErrorHandler, useClass: StaleBundleErrorHandler },
    // Load-bearing under zoneless, not optional polish: zone.js used to catch
    // unhandled errors and promise rejections and forward them to `ErrorHandler`
    // via `NgZone.onError`. Without zone.js nothing does, and
    // `StaleBundleErrorHandler` above only ever sees a failed chunk import
    // because it arrives as an unhandled rejection. This re-establishes that
    // path with two `window` listeners.
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(
      withInterceptorsFromDi(),
      // The chain is built with `reduceRight`, so entries later in this list sit
      // nearer the backend. `blobErrorInterceptor` stays last: it re-reads a
      // `Blob` error body as JSON, and everything that inspects `error.error` —
      // including `@pozi/authmodule`'s token refresh, which comes in through
      // `withInterceptorsFromDi()` above — has to see the parsed body.
      withInterceptors([
        languageInterceptor,
        httpErrorInterceptor,
        blobErrorInterceptor,
      ])
    ),
    provideAuth<CompanyUser, ICompanyUser>({
      // debug: true,
      //apiPrefix: 'https://pozi2.test.pozi.tech/api',
      apiPrefix: '/api/',
      authPrefix: 'auth/v1',
      loginEndpoint: '/login',
      userFactory: (raw: ICompanyUser) => new CompanyUser(raw),
      refreshTokenEndpoint: '/refresh-token',
      whitelistedEndpoints: [
        'auth/v1/oauth2/token',
        'auth/v1/refresh-token',
        'auth/v1/login',
        'system/v1/info',
      ],
      loginPath: 'login',
    }),
    provideOAuthClient({
      resourceServer: {
        allowedUrls: ['/api/auth/v1/oauth2'],
        sendAccessToken: true,
      },
    }),
    provideAppInitializer(() => {
      const initializerFn = downloadApiInfo(
        inject(HttpClient),
        inject(SharedVariablesService)
      );
      return initializerFn();
    }),
  ],
};

export function downloadApiInfo(
  http: HttpClient,
  sharedVars: SharedVariablesService
) {
  return (): Observable<ApiInfo> =>
    http
      .get<{
        googleMapsApiKey: string;
        oauthProviders: {
          issuer: string;
          clientId: string;
          scope: string;
          label: string;
        }[];
        apiVersion: { buildDate: string; version: string };
      }>('system/v1/info')
      .pipe(
        tap((apiInfo) => {
          sharedVars.setVar('googleMapsApiKey', apiInfo.googleMapsApiKey);
          sharedVars.setVar('oauthProviders', apiInfo.oauthProviders);
          sharedVars.setVar('apiVersion', apiInfo.apiVersion);
        })
      );
}
